/**
 * Reverse PITI Calculator
 * Given a desired total PITI payment, calculates the maximum home price
 */

interface ReversePitiInputs {
  desiredPiti: number;
  interestRate: number; // Annual percentage (e.g., 6.5)
  loanTerm: number; // Years
  downPaymentPercent: number; // Percentage (e.g., 20 for 20%)
  propertyTaxRate: number; // Annual percentage (e.g., 2.0)
  annualInsurance: number; // Annual dollar amount (e.g., 1800)
  hoaFees: number; // Monthly HOA
  pmiRate: number; // Annual percentage (e.g., 0.5) - only if down payment < 20%
  includePmi: boolean;
}

interface ReversePitiResult {
  maxHomePrice: number;
  loanAmount: number;
  downPayment: number;
  monthlyPrincipalInterest: number;
  monthlyPropertyTax: number;
  monthlyInsurance: number;
  monthlyHoa: number;
  monthlyPmi: number;
  totalPiti: number;
}

/**
 * Calculate the maximum home price given a target PITI payment
 * 
 * PITI = P&I + PropertyTax + Insurance + HOA + PMI
 * 
 * Where:
 * - P&I = Loan * (r(1+r)^n) / ((1+r)^n - 1)
 * - PropertyTax = HomePrice * (taxRate/100) / 12
 * - Insurance = annualInsurance / 12 (fixed dollar amount)
 * - PMI = LoanAmount * (pmiRate/100) / 12 (if applicable)
 * - Loan = HomePrice * (1 - downPaymentPercent/100)
 * 
 * Solving for HomePrice:
 * PITI - HOA - (annualInsurance/12) = HomePrice * [
 *   (1 - dpPct) * (r(1+r)^n) / ((1+r)^n - 1) +
 *   taxRate/12/100 +
 *   (1 - dpPct) * pmiRate/12/100
 * ]
 */
export function calculateReversePiti(inputs: ReversePitiInputs): ReversePitiResult {
  const {
    desiredPiti,
    interestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi
  } = inputs;

  // Monthly interest rate
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = loanTerm * 12;
  
  // Loan-to-value ratio (what percentage of home price is financed)
  const ltvRatio = (100 - downPaymentPercent) / 100;
  
  // Calculate the P&I factor per dollar of loan
  let piFactorPerLoanDollar = 0;
  if (monthlyRate > 0) {
    piFactorPerLoanDollar = 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  } else {
    piFactorPerLoanDollar = 1 / numberOfPayments;
  }
  
  // Monthly property tax per dollar of home price
  const monthlyTaxPerDollar = (propertyTaxRate / 100) / 12;
  
  // Monthly insurance is a fixed dollar amount (annual / 12)
  const monthlyInsuranceFixed = annualInsurance / 12;
  
  // Monthly PMI per dollar of loan (only if down payment < 20%)
  const monthlyPmiPerLoanDollar = includePmi && downPaymentPercent < 20 
    ? (pmiRate / 100) / 12 
    : 0;
  
  // Combined factor per dollar of home price
  // P&I + PMI are based on loan amount (ltvRatio * homePrice)
  // Tax is based on home price
  // Insurance is now a fixed amount, not per-dollar
  const combinedFactor = 
    (ltvRatio * piFactorPerLoanDollar) + // P&I per home price dollar
    monthlyTaxPerDollar + // Tax per home price dollar
    (ltvRatio * monthlyPmiPerLoanDollar); // PMI per home price dollar
  
  // PITI available after HOA and fixed insurance
  const pitiAfterFixedCosts = desiredPiti - hoaFees - monthlyInsuranceFixed;
  
  // Calculate max home price
  const maxHomePrice = combinedFactor > 0 ? pitiAfterFixedCosts / combinedFactor : 0;
  
  // Calculate the breakdown
  const loanAmount = maxHomePrice * ltvRatio;
  const downPayment = maxHomePrice * (downPaymentPercent / 100);
  
  let monthlyPrincipalInterest = 0;
  if (monthlyRate > 0) {
    monthlyPrincipalInterest = loanAmount * piFactorPerLoanDollar;
  } else {
    monthlyPrincipalInterest = loanAmount / numberOfPayments;
  }
  
  const monthlyPropertyTax = maxHomePrice * monthlyTaxPerDollar;
  const monthlyInsurance = monthlyInsuranceFixed;
  const monthlyPmi = includePmi && downPaymentPercent < 20
    ? loanAmount * monthlyPmiPerLoanDollar 
    : 0;
  
  const totalPiti = monthlyPrincipalInterest + monthlyPropertyTax + monthlyInsurance + hoaFees + monthlyPmi;
  
  return {
    maxHomePrice: Math.round(maxHomePrice),
    loanAmount: Math.round(loanAmount),
    downPayment: Math.round(downPayment),
    monthlyPrincipalInterest: Math.round(monthlyPrincipalInterest * 100) / 100,
    monthlyPropertyTax: Math.round(monthlyPropertyTax * 100) / 100,
    monthlyInsurance: Math.round(monthlyInsurance * 100) / 100,
    monthlyHoa: hoaFees,
    monthlyPmi: Math.round(monthlyPmi * 100) / 100,
    totalPiti: Math.round(totalPiti * 100) / 100
  };
}
